// m6-metadata-engine.js — API polling client for the rad1o.app backend.
// Polls GET /api/metadata?url=X every 15 seconds. Fires onTrackChange
// when the raw metadata string changes. No probing logic, no CORS handling.
// Exposes window.M6 = { init, setStation, onTrackChange, getAvailability, onAvailabilityChange }.

window.M6 = (() => {
  let _apiBase = null;
  let _trackCbs = [];
  let _availCb = null;
  let _availability = {};
  let _lastTrack = null;
  let _pollTimer = null;
  let _abortCtrl = null;
  let _currentIndex = null;
  let _currentUrl = null;
  let _lastPollTime = 0;
  let _heartbeatUnsub = null;

  const POLL_INTERVAL_MS = 15000;

  // ---------------------------------------------------------------------------
  // Internal: poll the backend API
  // ---------------------------------------------------------------------------

  async function _poll(index, url) {
    try {
      const res = await fetch(
        `${_apiBase}/api/metadata?url=${encodeURIComponent(url)}`,
        { signal: _abortCtrl?.signal }
      );
      if (!res.ok) return;
      // Guard: discard response if another setStation() call has changed the index.
      // This prevents stale poll responses from the old station from firing onTrackChange
      // after the user has already navigated to a new station.
      if (index !== _currentIndex) return;
      const data = await res.json();

      // Track availability changes
      const prev = _availability[index] || { available: false, source: null };
      const newAvail = !!data.available;
      if (prev.available !== newAvail && _availCb) {
        _availability[index] = { available: newAvail, source: data.source || null };
        _availCb({ index, available: newAvail, source: data.source || null });
      } else {
        _availability[index] = { available: newAvail, source: data.source || null };
      }

      // Fire track change only when metadata actually changed
      if (data.available && data.raw && data.raw !== _lastTrack) {
        _lastTrack = data.raw;
        _trackCbs.forEach(cb => cb({
          index,
          artist: data.artist || null,
          title: data.title || null,
          raw: data.raw,
          source: data.source || 'api',
        }));
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        // Network error — log for instrumentation, retry on next interval
        Logger.log({ ts: Date.now(), module: 'M6', fn: '_poll', inputs: { index, url }, output: null, durationMs: 0, level: 'debug', error: e });
      }
    }
  }

  function _startPolling(index, url) {
    _poll(index, url); // immediate first poll
    _lastPollTime = Date.now();

    // Use the audio element's timeupdate heartbeat instead of setInterval.
    // iOS throttles/suspends setInterval when the screen is locked, but the
    // audio element continues playing and firing timeupdate events. M1 fires
    // onHeartbeat on every timeupdate — we piggyback on this to keep polling
    // alive in the background so lock screen metadata stays current.
    if (window.M1 && M1.onHeartbeat) {
      _heartbeatUnsub = M1.onHeartbeat(function () {
        if (Date.now() - _lastPollTime >= POLL_INTERVAL_MS) {
          _lastPollTime = Date.now();
          _poll(index, url);
        }
      });
    }

    // Fallback: also keep a setInterval in case M1 isn't loaded or the
    // heartbeat stops (e.g. audio paused). The heartbeat is the primary
    // mechanism; this is a safety net for non-iOS platforms.
    _pollTimer = setInterval(function () {
      if (Date.now() - _lastPollTime >= POLL_INTERVAL_MS) {
        _lastPollTime = Date.now();
        _poll(index, url);
      }
    }, 5000);
  }

  // ---------------------------------------------------------------------------
  // Public interface (ARD M6 contract)
  // ---------------------------------------------------------------------------

  function init(apiBaseUrl) {
    _apiBase = apiBaseUrl || null;
    return true;
  }

  function setStation(index, url) {
    // Cancel any previous poll cycle
    if (_abortCtrl) { _abortCtrl.abort(); _abortCtrl = null; }
    if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
    if (_heartbeatUnsub) { _heartbeatUnsub(); _heartbeatUnsub = null; }
    _lastTrack = null;
    _currentIndex = index;
    _currentUrl = url;

    _abortCtrl = new AbortController();
    _startPolling(index, url);
    return true;
  }

  function onTrackChange(callback) {
    _trackCbs.push(callback);
    return () => {
      _trackCbs = _trackCbs.filter(cb => cb !== callback);
    };
  }

  /**
   * Force an immediate metadata poll for the current station.
   * Called by M8 when the app regains visibility/focus to
   * get fresh track data for the lock screen as fast as possible
   * after the user unlocks their device.
   */
  function forcePoll() {
    if (_currentIndex !== null && _currentUrl) {
      _poll(_currentIndex, _currentUrl);
    }
  }

  function getAvailability(index) {
    return _availability[index] || { available: false, source: null };
  }

  function onAvailabilityChange(callback) {
    _availCb = callback;
    return true;
  }

  return { init, setStation, onTrackChange, forcePoll, getAvailability, onAvailabilityChange };
})();
